<!-- eslint-disable vue/no-unused-refs -->
<template>
  <form
    class="search-bar form-group"
    @submit.prevent="handleFormSubmit"
  >
    <div class="form-group__field">
      <label
        class="visually-hidden"
        :for="`search-${namespace}`"
        v-text="$string('search.search_placeholder')"
      />

      <input
        :id="`search-${namespace}`"
        ref="input"
        v-model="input"
        class="search-bar__input text-body-l-regular text-body-s-regular-desktop"
        :class="{ [$class('active')]: searchQuery !== '' }"
        :placeholder="$string('search.search_placeholder')"
        type="search"
      >
    </div>

    <button
      v-if="searchQuery !== ''"
      class="search-bar__clear text-body-s-underline"
      type="button"
      @click.prevent="handleClear"
      v-text="$string('search.clear')"
    />

    <button
      class="search-bar__submit icon-button"
      type="submit"
      @blur="handleButtonBlur"
    >
      <span
        class="visually-hidden"
        v-text="$string('search.submit')"
      />

      <icon-right-arrow />
    </button>
  </form>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Search from snippet used in site header.
 * - Component settings: snippet / dynamic / global
 *
 * @param {String} namespace - Unique identifier.
 * @param {Object} redirect - Redirect URL.
 * @param {String} searchQuery - Current search query, used to update input
 * value when clicking suggestion.
 *
 */
import { mapState } from 'vuex'

import IconRightArrow from '~icons/directional-navigation/arrow/right.svg'

export default {
  name: 'SearchBar',

  components: {
    IconRightArrow,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    redirect: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },

  emits: {
    'search-focus': (payload) => typeof payload === 'boolean',
    'search-input': (payload) => typeof payload === 'string',
    'search-submit': () => true,
    clear: () => true,
  },

  data() {
    return {
      input: '',
      query: '',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      tabbable: (state) => state.accessibility.tabbable,
    }),
  },

  watch: {

    /**
     * Watch input to debounce update query.
     * - Debounces input to set query only when finished typing.
     * - Don't watch query to emit event because search query can update query
     *   when user clicks on a suggestion, don't want to trigger two updates.
     */
    input(query) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        this.query = query
        this.$emit('search-input', this.query)
      }, this.$timing('normal'))
    },

    /**
     * Watch search query to update query.
     */
    searchQuery() {
      this.input = this.searchQuery
    },
  },

  methods: {

    /**
     * Handle button blur.
     * - Assumes that if tabbable is active, there is no query, and button blurs
     *   then the user wants to tab outside the search bar so predictive search
     *   should close.
     */
    handleButtonBlur() {
      if (!this.tabbable || this.query) {
        return
      }

      this.$emit('search-focus', false)
    },

    /**
     * Handle form submit.
     * - If redirect is true, redirect to search page with query.
     * - If redirect is false, emit form submit.
     */
    handleFormSubmit() {
      if (!this.redirect) {
        this.$emit('search-submit')
        return
      }

      location.href = `${this.$variable('routes.search')}?q=${this.input}&type=product`
    },

    /**
     * Handle clear to emit event.
     */
    handleClear() {
      this.$emit('clear')
    },
  },
}
</script>
